import React from "react";
import { cn } from "@/designs/shadcn/lib/utils";

export interface ListProps {
  children: React.ReactNode;
  className?: string;
  numbered?: boolean; // when true, auto-prepends a numbered icon if none provided
}

export interface ListItemProps {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  right?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export function ListItem({
  icon,
  title,
  right,
  children,
  className,
}: ListItemProps) {
  return (
    <div className={cn("flex items-start space-x-4", className)}>
      {icon ? <div>{icon}</div> : null}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
          {right ? <div className="ml-3 mb-1 shrink-0">{right}</div> : null}
        </div>
        {children ? <p className="text-sm text-gray-600">{children}</p> : null}
      </div>
    </div>
  );
}

export default function List({
  children,
  className,
  numbered = false,
}: ListProps) {
  const count = React.Children.count(children);
  return (
    <div className={cn("space-y-4", className)}>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          const extra = index < count - 1 ? "mb-6" : undefined;
          const prevClass = (child.props as { className?: string }).className;

          // Auto-inject a numbered icon if requested and the child has no icon
          const prevIcon = (child.props as { icon?: React.ReactNode }).icon;
          const numberIcon = (
            <div className="max-w-[32px] min-w-[32px] max-h-[32px] min-h-[32px] bg-[#DAE0EB] flex justify-center items-center rounded-full">
              {index + 1}
            </div>
          );

          return React.cloneElement(
            child as React.ReactElement<{
              className?: string;
              icon?: React.ReactNode;
            }>,
            {
              className: cn(prevClass, extra),
              ...(numbered && !prevIcon ? { icon: numberIcon } : {}),
            },
          );
        }
        return child;
      })}
    </div>
  );
}
